import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import urls from "../../../../global/constants/UrlConstants";
import PersonalDetails from "../stepper/Personal Details/PersonalDetails";
import YhButton from "../../../../ui/YhButton";
import { getLeadById } from "../ShowAll/ShowAllService";

const EDIT_STORAGE_KEY = (id: string) => `all-lead-edit-${id}`;

const resolveLeadIdFromPath = (pathname: string, paramId?: string) => {
  if (paramId) return paramId;
  const match = pathname.match(
    /\/(?:all-leads|hometatva-leads|my-Lead)\/edit\/([^/?#]+)/
  );
  return match?.[1] || "";
};

const readCachedLead = (leadId: string) => {
  if (!leadId) return null;
  try {
    const raw = sessionStorage.getItem(EDIT_STORAGE_KEY(leadId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export type StepActions = {
  submit: () => void | Promise<void>;
};

interface CustomProps {
  isDialogOpen?: boolean;
  handleCloseAddTeam?: Function;
  userId?: string;
  setUserId?: Function;
  fromSettingOrLogout?: string;
  addNewTeamDialog?: any;
  eventForEdit?: any;
}

const AddNewTeam = (props: CustomProps = {}) => {
  const history = useHistory();
  const location = useLocation();
  const { id: paramId } = useParams<{ id?: string }>();
  const leadId = resolveLeadIdFromPath(location.pathname, paramId || props.userId);
  const isEmbedded = props.isDialogOpen !== undefined;
  const isHometatva = location.pathname.includes("/hometatva-leads");
  const isMyLead = location.pathname.includes("/my-Lead");
  const listPath = isHometatva
    ? urls.HOMETATVALEADFORMLIST
    : isMyLead
      ? urls.mylead
      : urls.ALLLEADFORMLIST;

  const [userId, setUserId] = useState<string>(props.userId || leadId || "");
  const [eventForEdit, setEventForEdit] = useState<any>(() => {
    if (props.eventForEdit) return props.eventForEdit;
    return readCachedLead(leadId);
  });
  const [saving, setSaving] = useState(false);
  const [loadingLead, setLoadingLead] = useState(false);
  const stepActionsRef = useRef<StepActions | null>(null);

  const isEdit = !!(leadId || props.addNewTeamDialog?.edit || props.userId);
  const addNewTeamDialog = props.addNewTeamDialog || {
    open: true,
    edit: isEdit,
  };

  useEffect(() => {
    if (props.userId) setUserId(props.userId);
    else if (leadId) setUserId(leadId);
  }, [props.userId, leadId]);

  useEffect(() => {
    let cancelled = false;

    const loadLead = async () => {
      if (props.eventForEdit) {
        setEventForEdit(props.eventForEdit);
        return;
      }
      if (!leadId) {
        setEventForEdit(null);
        return;
      }

      const cached = readCachedLead(leadId);
      if (cached) {
        setEventForEdit(cached);
        return;
      }

      try {
        setLoadingLead(true);
        const response = await getLeadById(leadId);
        if (cancelled) return;
        const raw = response?.data;
        const lead = Array.isArray(raw) ? raw[0] : raw;
        if (response?.success !== 0 && lead && (lead._id || lead.id)) {
          setEventForEdit(lead);
          try {
            sessionStorage.setItem(
              EDIT_STORAGE_KEY(leadId),
              JSON.stringify(lead)
            );
          } catch {
            // ignore storage failures
          }
        } else {
          setEventForEdit(null);
        }
      } catch {
        if (!cancelled) setEventForEdit(null);
      } finally {
        if (!cancelled) setLoadingLead(false);
      }
    };

    loadLead();
    return () => {
      cancelled = true;
    };
  }, [leadId, props.eventForEdit]);

  const registerStepActions = useCallback((actions: StepActions) => {
    stepActionsRef.current = actions;
  }, []);

  const handleClose = () => {
    if (props.handleCloseAddTeam) {
      props.handleCloseAddTeam();
      return;
    }
    if (leadId) {
      sessionStorage.removeItem(EDIT_STORAGE_KEY(leadId));
    }
    history.push(listPath);
  };

  const updateUserId = (nextId: string) => {
    setUserId(nextId);
    props.setUserId?.(nextId);
  };

  const handleFooterSave = async () => {
    if (!stepActionsRef.current?.submit) return;
    try {
      setSaving(true);
      await stepActionsRef.current.submit();
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit ? "Edit Lead" : "Add Lead";
  const subtitle = isEdit
    ? "Update lead details and save your changes."
    : "Fill in the form to create a new lead.";

  const stickyFooter = (
    <div className="sticky bottom-0 z-20 -mx-4 mt-auto flex shrink-0 items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white/95 px-4 py-3 backdrop-blur sm:-mx-5 sm:px-5 lg:-mx-6 lg:px-6">
      <button
        type="button"
        onClick={handleClose}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#374151] shadow-sm transition hover:bg-[#F3F4F6]"
        aria-label="Back"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <div className="flex shrink-0 items-center gap-2">
        <YhButton variant="secondary" size="sm" onClick={handleClose}>
          Back
        </YhButton>
        <YhButton
          variant="primary"
          size="sm"
          loading={saving}
          onClick={handleFooterSave}
        >
          {isEdit ? "Edit & Save" : "Save"}
        </YhButton>
      </div>
    </div>
  );

  const formBody = (
    <div
      className={`relative flex flex-col overflow-hidden bg-white p-4 pb-0 sm:p-5 sm:pb-0 lg:p-6 lg:pb-0 ${
        isEmbedded
          ? "h-[min(90vh,880px)] w-full max-w-6xl"
          : "h-[calc(100vh-68px)] max-h-[calc(100vh-68px)]"
      }`}
    >
      <div className="mb-4 flex shrink-0 items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
            {title}
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">{subtitle}</p>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden pb-20">
        <section className="h-full min-h-0 overflow-auto rounded-2xl border border-[#EEF0F5] bg-white p-4 shadow-[0_4px_20px_rgba(15,23,42,0.04)] sm:p-6">
          {loadingLead && !eventForEdit ? (
            <p className="text-sm text-[#6B7280]">Loading lead details…</p>
          ) : (
            <PersonalDetails
              userId={userId}
              handleCloseAddTeam={handleClose}
              setUserId={updateUserId}
              addNewTeamDialog={addNewTeamDialog}
              fromSettingOrLogout={props.fromSettingOrLogout}
              eventForEdit={eventForEdit}
              registerStepActions={registerStepActions}
              hideFooterButtons
            />
          )}
        </section>
      </div>

      {stickyFooter}
    </div>
  );

  if (isEmbedded) {
    if (!props.isDialogOpen) return null;
    return (
      <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40 p-4">
        <div className="w-full max-w-6xl overflow-hidden rounded-2xl shadow-xl">
          {formBody}
        </div>
      </div>
    );
  }

  return formBody;
};

export default AddNewTeam;
